import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { type FormEvent, type JSX, useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AppLoader } from '~/components/loader'
import { UsersCommandList } from '~/features/command-lists'
import { AlertMutators } from '~/mutators/alerts'
import { RouterOutput, useTRPC } from '~/services/trpc'
import { SettingsContent, type TitleSegment, useSettingTitle } from '~/templates/settings'

import {
  AlertDialog,
  AlertDialogTrigger,
  Button,
  Form,
  FormControl,
  FormField,
  FormFieldInput,
  FormFieldMultiline,
  FormFieldSelect,
  FormFooterControl,
  FormItem,
  FormLabel,
  FormMessage,
  Heading2,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  processFormError,
} from '@hap/components'

type Alert = RouterOutput['alerts']['getById'] & {
  dailyDigestEnabled: boolean
  dailyDigestTime: string
  dailyDigestTimezone: string
  weeklyDigestEnabled: boolean
  weeklyDigestDay: number
  weeklyDigestTime: string
  weeklyDigestTimezone: string
}

export const Route = createFileRoute('/_authenticated/$alias/settings/alerts/$alertId')({
  component: AlertSettings,
})

function AlertSettings(): JSX.Element {
  const { alertId } = Route.useParams()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()
  const { data, isLoading } = useQuery(trpc.alerts.getById.queryOptions(alertId))
  const alert = data as Alert | undefined

  const navigateToAlerts = useCallback(() => {
    void navigate({ to: '/$alias/settings/alerts', search: location.search })
  }, [location.search, navigate])

  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [{ label: 'Alerts', onClick: navigateToAlerts }, alert?.name].filter(
        Boolean,
      ) as TitleSegment[],
    [alert?.name, navigateToAlerts],
  )
  useSettingTitle(titleSegments)

  if (isLoading || !alert) return <AppLoader />

  return (
    <SettingsContent className="w-full overflow-y-auto">
      <AlertDetailsForm alert={alert} />
      <DailyDigestForm alert={alert} />
      <WeeklyDigestForm alert={alert} />
      <SubscriberSection alert={alert} />
      <DangerZone
        alertId={alert.id}
        alertName={alert.name}
        onNavigateBack={navigateToAlerts}
      />
    </SettingsContent>
  )
}

const alertSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().max(2000),
})

const digestTimeSchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/, 'Use HH:mm or HH:mm:ss format')

const dailyDigestSchema = z.object({
  dailyDigestEnabled: z.boolean(),
  dailyDigestTime: digestTimeSchema,
  dailyDigestTimezone: z.string().min(1, 'Timezone is required'),
})

const weeklyDigestSchema = z.object({
  weeklyDigestEnabled: z.boolean(),
  weeklyDigestDay: z.enum(['0', '1', '2', '3', '4', '5', '6']),
  weeklyDigestTime: digestTimeSchema,
  weeklyDigestTimezone: z.string().min(1, 'Timezone is required'),
})

const weekdays = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
]

const timezones = Intl.supportedValuesOf('timeZone').map((timezone) => ({
  label: timezone.replace(/_/g, ' ').replace('/', ' / '),
  value: timezone,
}))

function AlertDetailsForm({ alert }: { alert: Alert }): JSX.Element {
  const { update, isPending } = AlertMutators.useUpdate()
  const values = useMemo(
    () => ({ name: alert.name, description: alert.description ?? '' }),
    [alert.description, alert.name],
  )
  const form = useForm({
    values,
    resolver: zodResolver(alertSchema),
  })

  const handleSubmit = form.handleSubmit(async (formValues) => {
    try {
      await update({
        id: alert.id,
        name: formValues.name,
        description: formValues.description.trim() || null,
      })
    } catch (error) {
      processFormError(form.setError, error)
    }
  })

  return (
    <section className="space-y-6">
      <div>
          <Heading2>Alert details</Heading2>
          <p className="text-accent-foreground">
            This alert sends an email when the organization contributes to a service issue
            or an agent misses or worsens one.
          </p>
      </div>
      <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
        <div className="space-y-1">
          <Label className="font-semibold">Alert status</Label>
          <p className="text-accent-foreground">
            {alert.enabledAt ? 'Notifications are being sent.' : 'Notifications are paused.'}
          </p>
        </div>
        <Switch
          checked={Boolean(alert.enabledAt)}
          disabled={isPending}
          onCheckedChange={async (checked) => {
            try {
              await update({ id: alert.id, enabledAt: checked ? new Date() : null })
            } catch {
              // The mutator displays the failure message.
            }
          }}
          aria-label="Enable alert"
        />
      </div>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormFieldInput control={form.control} name="name" label="Name" />
          <FormFieldMultiline
            control={form.control}
            name="description"
            label="Description"
            placeholder="Optional context for other administrators"
          />
          <FormFooterControl submitLabel="Save changes" onReset={() => form.reset(values)} />
        </form>
      </Form>
    </section>
  )
}

function DailyDigestForm({ alert }: { alert: Alert }): JSX.Element {
  const { update } = AlertMutators.useUpdate()
  const values = useMemo(
    () => ({
      dailyDigestEnabled: alert.dailyDigestEnabled,
      dailyDigestTime: alert.dailyDigestTime,
      dailyDigestTimezone: alert.dailyDigestTimezone,
    }),
    [alert.dailyDigestEnabled, alert.dailyDigestTime, alert.dailyDigestTimezone],
  )
  const form = useForm({
    values,
    resolver: zodResolver(dailyDigestSchema),
  })

  const handleSubmit = form.handleSubmit(async (formValues) => {
    try {
      await update({
        id: alert.id,
        dailyDigestEnabled: formValues.dailyDigestEnabled,
        dailyDigestTime: formValues.dailyDigestTime,
        dailyDigestTimezone: formValues.dailyDigestTimezone,
      })
    } catch (error) {
      processFormError(form.setError, error)
    }
  })

  return (
    <section className="space-y-6">
      <div>
        <Heading2>Daily digest</Heading2>
        <p className="text-accent-foreground">
          Send subscribers a daily summary for this alert at the selected local time.
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
            <div className="space-y-1">
              <Label className="font-semibold">Enable daily digest</Label>
              <p className="text-accent-foreground">
                {form.watch('dailyDigestEnabled')
                  ? 'The daily summary will be sent on this schedule.'
                  : 'Daily summary emails are disabled.'}
              </p>
            </div>
            <FormField
              control={form.control}
              name="dailyDigestEnabled"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Enable daily digest"
                />
              )}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="dailyDigestTime"
              render={({ field }) => (
                <FormItem>
                  <div className="group relative flex flex-col gap-2">
                    <FormLabel>Local time</FormLabel>
                    <FormControl>
                      <Input {...field} type="time" step={1} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormFieldSelect
              control={form.control}
              name="dailyDigestTimezone"
              label="Timezone"
              options={timezones}
            />
          </div>
          <FormFooterControl
            submitLabel="Save daily digest settings"
            onReset={() => form.reset(values)}
          />
        </form>
      </Form>
    </section>
  )
}

function WeeklyDigestForm({ alert }: { alert: Alert }): JSX.Element {
  const { update } = AlertMutators.useUpdate()
  const values = useMemo(
    () => ({
      weeklyDigestEnabled: alert.weeklyDigestEnabled,
      weeklyDigestDay: String(alert.weeklyDigestDay) as z.infer<
        typeof weeklyDigestSchema
      >['weeklyDigestDay'],
      weeklyDigestTime: alert.weeklyDigestTime,
      weeklyDigestTimezone: alert.weeklyDigestTimezone,
    }),
    [
      alert.weeklyDigestDay,
      alert.weeklyDigestEnabled,
      alert.weeklyDigestTime,
      alert.weeklyDigestTimezone,
    ],
  )
  const form = useForm({
    values,
    resolver: zodResolver(weeklyDigestSchema),
  })

  const handleSubmit = form.handleSubmit(async (formValues) => {
    try {
      await update({
        id: alert.id,
        weeklyDigestEnabled: formValues.weeklyDigestEnabled,
        weeklyDigestDay: Number(formValues.weeklyDigestDay),
        weeklyDigestTime: formValues.weeklyDigestTime,
        weeklyDigestTimezone: formValues.weeklyDigestTimezone,
      })
    } catch (error) {
      processFormError(form.setError, error)
    }
  })

  return (
    <section className="space-y-6">
      <div>
        <Heading2>Weekly digest</Heading2>
        <p className="text-accent-foreground">
          Send subscribers a weekly summary for this alert on the selected schedule.
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
            <div className="space-y-1">
              <Label className="font-semibold">Enable weekly digest</Label>
              <p className="text-accent-foreground">
                {form.watch('weeklyDigestEnabled')
                  ? 'The weekly summary will be sent on this schedule.'
                  : 'Weekly summary emails are disabled.'}
              </p>
            </div>
            <FormField
              control={form.control}
              name="weeklyDigestEnabled"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Enable weekly digest"
                />
              )}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <FormFieldSelect
              control={form.control}
              name="weeklyDigestDay"
              label="Weekday"
              options={weekdays}
            />
            <FormField
              control={form.control}
              name="weeklyDigestTime"
              render={({ field }) => (
                <FormItem>
                  <div className="group relative flex flex-col gap-2">
                    <FormLabel>Time</FormLabel>
                    <FormControl>
                      <Input {...field} type="time" step={1} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormFieldSelect
              control={form.control}
              name="weeklyDigestTimezone"
              label="Timezone"
              options={timezones}
            />
          </div>
          <FormFooterControl
            submitLabel="Save digest settings"
            onReset={() => form.reset(values)}
          />
        </form>
      </Form>
    </section>
  )
}

function SubscriberSection({ alert }: { alert: Alert }): JSX.Element {
  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Heading2>Subscribers</Heading2>
          <p className="text-accent-foreground">
            Choose members or email addresses that should receive this alert. During the pilot,
            delivery is routed only to Robert.
          </p>
        </div>
        <AddSubscriber alertId={alert.id} />
      </div>
      {alert.subscribers.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed px-4 py-8 text-center">
          No subscribers yet.
        </div>
      ) : (
        <div className="divide-y rounded-lg border">
          {alert.subscribers.map((subscriber) => (
            <SubscriberRow key={subscriber.id} alertId={alert.id} subscriber={subscriber} />
          ))}
        </div>
      )}
    </section>
  )
}

function AddSubscriber({ alertId }: { alertId: string }): JSX.Element {
  const [email, setEmail] = useState('')
  const { addSubscriber, isPending } = AlertMutators.useAddSubscriber()

  const addEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const recipient = z.string().trim().email().safeParse(email)
    if (!recipient.success) {
      toast.error('Enter a valid email address')
      return
    }
    try {
      await addSubscriber({ alertId, recipient: { email: recipient.data } })
      setEmail('')
    } catch {
      // The mutator displays the failure message.
    }
  }

  const addUser = async (userIds: string[]) => {
    const userId = userIds[0]
    if (!userId) return
    try {
      await addSubscriber({ alertId, recipient: { userId } })
    } catch {
      // The mutator displays the failure message.
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" icon="plus">
          Add subscriber
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <Tabs defaultValue="email">
          <TabsList className="w-full">
            <TabsTrigger value="email">Email address</TabsTrigger>
            <TabsTrigger value="member">Member</TabsTrigger>
          </TabsList>
          <TabsContent value="email" className="p-3">
            <form onSubmit={addEmail} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                aria-label="Subscriber email address"
              />
              <Button type="submit" loading={isPending} loadingLabel="Adding...">
                Add
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="member" className="m-0">
            <UsersCommandList onSelect={addUser} placeholder="Search members" />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}

function SubscriberRow({
  alertId,
  subscriber,
}: {
  alertId: string
  subscriber: Alert['subscribers'][number]
}): JSX.Element {
  const { updateSubscriber: enableSubscriber, isPending: isEnabling } =
    AlertMutators.useEnableSubscriber()
  const { updateSubscriber: disableSubscriber, isPending: isDisabling } =
    AlertMutators.useDisableSubscriber()
  const active = Boolean(subscriber.enabledAt) && !subscriber.disabledAt && !subscriber.unsubscribedAt
  const status = subscriber.unsubscribedAt ? 'Unsubscribed' : active ? 'Active' : 'Paused'

  const toggleSubscriber = async () => {
    try {
      if (active) await disableSubscriber({ alertId, subscriberId: subscriber.id })
      else await enableSubscriber({ alertId, subscriberId: subscriber.id })
    } catch {
      // The mutator displays the failure message.
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0">
        <p className="truncate font-medium">{subscriber.email}</p>
        <p className="text-accent-foreground mt-1 text-sm">
          {subscriber.userId ? 'Member' : 'Email address'} · {status}
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        loading={isEnabling || isDisabling}
        loadingLabel={active ? 'Pausing...' : 'Resuming...'}
        onClick={toggleSubscriber}
      >
        {active ? 'Pause' : 'Resume'}
      </Button>
    </div>
  )
}

function DangerZone({
  alertId,
  alertName,
  onNavigateBack,
}: {
  alertId: string
  alertName: string
  onNavigateBack: () => void
}): JSX.Element {
  const { deleteAlert, isPending } = AlertMutators.useDelete({ onSuccess: onNavigateBack })

  return (
    <section className="space-y-6 pb-12">
      <Heading2 className="text-destructive">Danger Zone</Heading2>
      <div className="border-destructive flex items-center justify-between gap-6 rounded-lg border p-4">
        <div className="space-y-1">
          <Label className="font-semibold">Delete this alert</Label>
          <p className="text-accent-foreground">
            Subscribers will stop receiving notifications from this alert.
          </p>
        </div>
        <AlertDialog
          title={`Delete ${alertName}`}
          description="This action cannot be undone."
          action={{
            onClick: async () => {
              try {
                await deleteAlert(alertId)
              } catch {
                // The mutator displays the failure message.
              }
            },
            value: 'Delete alert',
            variant: 'destructive',
          }}
        >
          <AlertDialogTrigger asChild>
            <Button variant="ghost-destructive" loading={isPending} icon="trash">
              Delete
            </Button>
          </AlertDialogTrigger>
        </AlertDialog>
      </div>
    </section>
  )
}
